'use client'

import { useState } from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { PlatformMark } from '../shell/PlatformMark'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'

// name_code + logo_url: a platform (the catalog's rows carry both), shown
// with its mark (PlatformMark) in the list and on the trigger.
export type SearchableSelectOption = { id: number | string; name: string; name_code?: string | null; logo_url?: string | null }

const optionMark = (o: SearchableSelectOption | undefined) => (o?.name_code ? <PlatformMark code={o.name_code} label={o.name} logoUrl={o.logo_url} /> : null)

// Generic searchable <select> replacement, built on the same Popover + cmdk
// Command primitives every other combobox in this app already ships
// (src/components/ui/command.tsx, .../popover.tsx) — styled through
// watchClasses instead of shadcn's default CSS vars so it drops in next to
// plain <select>s/<Input>s without looking out of place. Used by
// AssignFieldsWidget for the platform/message-type catalogs, which can run
// long enough that scrolling a plain <select> is worse than typing to filter.
export function SearchableSelect({
  theme,
  value,
  options,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  clearable = true,
  noneLabel,
  allowCustom = false,
}: {
  theme: ProcessedTheme
  value?: number | string | null
  options: SearchableSelectOption[]
  onChange: (id: string | undefined, text?: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  clearable?: boolean
  /** When set, adds a selectable "none" row (e.g. "No platform") at the top of the list, so clearing the field is discoverable without relying on the X icon. Selecting it behaves exactly like the clear button: onChange(undefined, undefined). */
  noneLabel?: string
  /** When set, typing something that isn't an option offers a "Use “…”" row that selects the typed text itself: onChange(text, text). The trigger then shows a value that's not in `options` as-is. For fields whose value is a free-text string with a suggestion list (e.g. the Workbench's Source), not an id from a catalog. */
  allowCustom?: boolean
}) {
  const c = watchClasses(theme)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // cmdk reorders its items' DOM nodes while it filters and React never puts
  // them back, so after a search the list (and the noneLabel row) stayed
  // shuffled on the next open. A fresh Command per open restores the order.
  const [openCount, setOpenCount] = useState(0)
  const selected = options.find((o) => String(o.id) === String(value))
  // With allowCustom, a value chosen via the "Use …" row (or set by the
  // caller) has no matching option — still render it on the trigger.
  const selectedLabel = selected ? selected.name : allowCustom && value != null && value !== '' ? String(value) : undefined
  const trimmedQuery = query.trim()
  const customCandidate = allowCustom && trimmedQuery && !options.some((o) => o.name.toLowerCase() === trimmedQuery.toLowerCase())
    ? trimmedQuery
    : undefined

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (next) setOpenCount((n) => n + 1); else setQuery('') }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={`flex h-8 w-full items-center justify-between gap-2 rounded-md border px-2 text-[12px] ${c.input}`}
        >
          <span className={cn('flex min-w-0 items-center gap-2 text-left', !selectedLabel && c.faint)}>{optionMark(selected)}<span className="truncate">{selectedLabel ?? placeholder}</span></span>
          <span className="flex shrink-0 items-center gap-1">
            {clearable && selectedLabel && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(undefined, undefined)
                }}
                className={`rounded p-0.5 hover:text-destructive ${c.faint}`}
                aria-label="Clear selection"
              >
                <X className="h-3 w-3" />
              </span>
            )}
            <ChevronsUpDown className={`h-3.5 w-3.5 ${c.faint}`} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command key={openCount}>
          <CommandInput placeholder={searchPlaceholder} className="text-[12px]" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {customCandidate && (
                <CommandItem
                  value={customCandidate}
                  onSelect={() => {
                    onChange(customCandidate, customCandidate)
                    setQuery('')
                    setOpen(false)
                  }}
                  className="text-[12px]"
                >
                  <Check className={cn('h-3.5 w-3.5', String(value) === customCandidate ? 'opacity-100' : 'opacity-0')} />
                  <span>Use “{customCandidate}”</span>
                </CommandItem>
              )}
              {noneLabel && (
                <CommandItem
                  value={noneLabel}
                  onSelect={() => {
                    onChange(undefined, undefined)
                    setQuery('')
                    setOpen(false)
                  }}
                  className="text-[12px]"
                >
                  <Check className={cn('h-3.5 w-3.5', !selected ? 'opacity-100' : 'opacity-0')} />
                  <span className={c.faint}>{noneLabel}</span>
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={o.name}
                  onSelect={() => {
                    onChange(String(o.id), o.name)
                    setQuery('')
                    setOpen(false)
                  }}
                  className="text-[12px]"
                >
                  <Check className={cn('h-3.5 w-3.5', String(o.id) === String(value) ? 'opacity-100' : 'opacity-0')} />
                  {optionMark(o)}
                  {o.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
